import { useEffect, useState } from "react";
import { api } from "../api";
import { Pill, actionPillKind, actionLabel } from "../components/Pill";
import { InstallBanner } from "../components/InstallBanner";
import { RedactedPreview } from "../components/RedactedPreview";
import { RiskPill } from "../components/RiskPill";
import { ToolBadge, toolLabel } from "../components/ToolBadge";
import { ActivityChart } from "../components/ActivityChart";

export function OverviewPage() {
  const [stats, setStats] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);

  const [chartEvents, setChartEvents] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      const [s, recent, wide] = await Promise.all([
        api.workspaceStats(),
        api.listEvents({ limit: 10 }),
        api.listEvents({ limit: 200 }),
      ]);
      setStats(s);
      setEvents(recent.events);
      setChartEvents(wide.events);
    })();
  }, []);

  if (!stats) return <div className="sg-empty">Loading…</div>;

  return (
    <>
      <header className="sg-page-header">
        <h1 className="sg-page-title">Overview</h1>
        <div className="sg-page-sub">Last 7 Days · {stats.total} Events</div>
      </header>

      <InstallBanner />

      <ActivityChart events={chartEvents} />

      <div className="sg-metric-strip">
        <Metric label="Total Prompts" value={stats.total} accent="neutral" />
        <Metric label="Allowed"       value={stats.counts.allow} accent="allow" />
        <Metric label="Warned"        value={stats.counts.warn + stats.counts.suggest_rewrite} accent="warn" />
        <Metric label="Blocked"       value={stats.counts.block} accent="block" />
      </div>

      <div className="sg-grid sg-grid-3" style={{ marginBottom: 30 }}>
        <TopCard title="Top Triggered Rules" items={(stats.topRules ?? []).map((r: any) => ({ label: titleCaseRule(r.name), count: r.count }))} />
        <TopCard title="Most Active Users"  items={(stats.topUsers ?? []).map((u: any) => ({ label: u.email, count: u.count }))} />
        <TopCard title="AI Tools Used"      items={(stats.topTools ?? []).map((t: any) => ({ label: toolLabel(t.tool), count: t.count }))} />
      </div>

      <div className="sg-section-label">Recent Activity</div>
      <div className="sg-log">
        {events.length === 0 && <FreshWorkspaceCard />}
        {events.map((e) => (
          <div key={e.id} className={`sg-record is-${actionPillKind(e.actionResult)}`}>
            <div className="sg-record-meta">
              <div className="sg-record-time">{new Date(e.createdAt).toLocaleTimeString()}</div>
              <div className="sg-record-tool"><ToolBadge tool={e.destinationTool} size={12} /></div>
            </div>
            <div className="sg-record-body">
              <div className="sg-record-title">{e.explanation}</div>
              <RedactedPreview previewText={e.previewText} fallback={e.maskedPreview} />
            </div>
            <div className="sg-record-actions">
              <Pill kind={actionPillKind(e.actionResult)}>{actionLabel(e.actionResult)}</Pill>
              <RiskPill score={e.riskScore} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/** Fresh-workspace card. Shown on Overview while events.length === 0.
 *  Replaces the old "No activity yet" stub with a four-step checklist
 *  that links to where each step actually lives in the dashboard. */
function FreshWorkspaceCard() {
  return (
    <div className="sg-fresh-card">
      <div className="sg-fresh-head">
        <div>
          <div className="sg-fresh-eyebrow">Welcome to Ventrin</div>
          <h2 className="sg-fresh-title">Get protection live in four steps.</h2>
          <p className="sg-fresh-sub">
            Each one takes under a minute. Come back here when you have activity to review.
          </p>
        </div>
      </div>
      <ol className="sg-fresh-steps">
        <li>
          <div>
            <h4>1. Install the Chrome extension</h4>
            <p>This is the layer that scans prompts in your team's browsers before they reach ChatGPT, Claude, Gemini or Copilot.</p>
          </div>
          <a className="sg-btn sg-btn-primary sg-btn-sm" href="https://www.ventrin.com/download.html" target="_blank" rel="noopener">Download</a>
        </li>
        <li>
          <div>
            <h4>2. Tune your policies</h4>
            <p>Choose which packs are active and add any client or matter names that should always be anonymised.</p>
          </div>
          <a className="sg-btn sg-btn-sm" href="/policies">Open Policies</a>
        </li>
        <li>
          <div>
            <h4>3. Invite your team</h4>
            <p>Generate workspace invites. Members redeem them inside the extension to join.</p>
          </div>
          <a className="sg-btn sg-btn-sm" href="/users">Open Users</a>
        </li>
        <li>
          <div>
            <h4>4. Add API access (optional)</h4>
            <p>Building an internal AI app? Generate a key and point your code at the Ventrin gateway instead of the provider.</p>
          </div>
          <a className="sg-btn sg-btn-sm" href="/api-access">Open API Access</a>
        </li>
      </ol>
    </div>
  );
}

const SMALL_WORDS = new Set(["and", "or", "of", "for", "to", "in", "on", "a", "an", "the", "with", "by", "at"]);
function titleCaseRule(s: string): string {
  if (!s) return s;
  return s.split(" ").map((w, i) => {
    if (i > 0 && SMALL_WORDS.has(w.toLowerCase())) return w.toLowerCase();
    if (/^[A-Z]{2,}$/.test(w)) return w; // preserve acronyms like API, IP, DOB
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(" ");
}

function TopCard({ title, items }: { title: string; items: { label: string; count: number }[] }) {
  return (
    <div className="sg-panel">
      <div className="sg-panel-head"><div className="sg-panel-title">{title}</div></div>
      <div className="sg-panel-pad">
        {items.length === 0 ? (
          <div className="sg-muted" style={{ fontSize: 12, padding: "6px 0" }}>No data in this range.</div>
        ) : (
          <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {items.map((it, i) => (
              <li key={i} className="sg-spread" style={{ padding: "7px 0", borderBottom: i < items.length - 1 ? "1px dashed var(--sg-line-soft)" : "0", fontSize: 13 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 12 }}>
                  <span style={{ color: "var(--sg-ink-4)", marginRight: 8, fontVariantNumeric: "tabular-nums" }}>{String(i + 1).padStart(2, "0")}</span>
                  {it.label}
                </span>
                <span style={{ fontFamily: "var(--sg-font-serif)", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{it.count}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, accent }: { label: string; value: number; accent: "allow" | "warn" | "block" | "neutral" }) {
  return (
    <div className="sg-metric">
      <div className="sg-metric-label">{label}</div>
      <div className="sg-metric-value">{value}</div>
      <div className={`sg-metric-accent ${accent}`} />
    </div>
  );
}
